import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApi } from "../utils/api";
import { useUserInfo } from "../utils/user";
import { useAuth } from "../utils/auth";

const WorkoutImportJobsContext = createContext(null);

const RECENT_JOBS_STORAGE_KEY = "trainerAppWorkoutImportRecentJobs_v1";
const MAX_RECENT_JOBS = 20;

function loadRecentJobsFromStorage() {
  if (typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(RECENT_JOBS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => x && typeof x.jobId === "string")
      .slice(0, MAX_RECENT_JOBS);
  } catch {
    return [];
  }
}

function saveRecentJobsToStorage(items) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      RECENT_JOBS_STORAGE_KEY,
      JSON.stringify(items.slice(0, MAX_RECENT_JOBS)),
    );
  } catch {
    /* ignore quota */
  }
}

export function WorkoutImportJobsProvider({ children }) {
  const {
    listActiveWorkoutImportJobs,
    getWorkoutImportJob,
    createWorkoutImportJob,
    cancelWorkoutImportJob,
  } = useApi();
  const { isCoach, loading: userLoading } = useUserInfo();
  const { user } = useAuth();
  const [activeJobs, setActiveJobs] = useState([]);
  const [recentImportJobs, setRecentImportJobs] = useState(
    loadRecentJobsFromStorage,
  );
  const prevActiveJobIdsRef = useRef(new Set());
  const locallyCancelledJobIdsRef = useRef(new Set());

  const rememberImportJob = useCallback((jobId, meta = {}) => {
    if (!jobId || typeof jobId !== "string") return;
    const status = meta.status ?? "unknown";
    setRecentImportJobs((prev) => {
      const rest = prev.filter((x) => x.jobId !== jobId);
      const next = [
        {
          jobId,
          lastKnownStatus: status,
          updatedAt: Date.now(),
        },
        ...rest,
      ].slice(0, MAX_RECENT_JOBS);
      saveRecentJobsToStorage(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!user || userLoading || !isCoach) {
      setActiveJobs([]);
      prevActiveJobIdsRef.current = new Set();
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await listActiveWorkoutImportJobs();
        if (cancelled) return;
        const jobs = (res?.jobs ?? []).filter(
          (j) => j?.jobId && !locallyCancelledJobIdsRef.current.has(j.jobId),
        );
        const serverIds = new Set(
          (res?.jobs ?? []).map((j) => j.jobId).filter(Boolean),
        );
        for (const id of [...locallyCancelledJobIdsRef.current]) {
          if (!serverIds.has(id)) locallyCancelledJobIdsRef.current.delete(id);
        }
        const newIds = new Set(jobs.map((j) => j.jobId).filter(Boolean));
        const prev = prevActiveJobIdsRef.current;
        for (const id of prev) {
          if (!newIds.has(id)) {
            try {
              const job = await getWorkoutImportJob(id);
              if (
                !cancelled &&
                job?.jobId &&
                (job.status === "completed" ||
                  job.status === "failed" ||
                  job.status === "cancelled")
              ) {
                rememberImportJob(id, { status: job.status });
              }
            } catch {
              /* ignore */
            }
          }
        }
        prevActiveJobIdsRef.current = newIds;
        setActiveJobs(jobs);
      } catch (err) {
        console.error(err);
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [
    user,
    userLoading,
    isCoach,
    listActiveWorkoutImportJobs,
    getWorkoutImportJob,
    rememberImportJob,
  ]);

  const fetchJob = useCallback(
    async (jobId) => getWorkoutImportJob(jobId),
    [getWorkoutImportJob],
  );

  const createImportJob = useCallback(
    async (payload) => {
      const res = await createWorkoutImportJob(payload);
      if (res?.jobId) {
        rememberImportJob(res.jobId, { status: "pending" });
        prevActiveJobIdsRef.current.add(res.jobId);
      }
      return res;
    },
    [createWorkoutImportJob, rememberImportJob],
  );

  const cancelImportJob = useCallback(
    async (jobId) => {
      if (!jobId || typeof jobId !== "string") return null;
      locallyCancelledJobIdsRef.current.add(jobId);
      setActiveJobs((prev) => prev.filter((j) => j.jobId !== jobId));
      prevActiveJobIdsRef.current.delete(jobId);
      rememberImportJob(jobId, { status: "cancelled" });
      try {
        const res = await cancelWorkoutImportJob(jobId);
        const status = res?.status;
        if (status === "completed" || status === "failed") {
          rememberImportJob(jobId, { status });
        } else {
          rememberImportJob(jobId, { status: status ?? "cancelled" });
        }
        return res;
      } catch (err) {
        locallyCancelledJobIdsRef.current.delete(jobId);
        console.error(err);
        throw err;
      }
    },
    [cancelWorkoutImportJob, rememberImportJob],
  );

  const value = useMemo(
    () => ({
      activeJobs,
      recentImportJobs,
      fetchJob,
      createImportJob,
      cancelImportJob,
      rememberImportJob,
    }),
    [
      activeJobs,
      recentImportJobs,
      fetchJob,
      createImportJob,
      cancelImportJob,
      rememberImportJob,
    ],
  );

  return (
    <WorkoutImportJobsContext.Provider value={value}>
      {children}
    </WorkoutImportJobsContext.Provider>
  );
}

export function useWorkoutImportJobs() {
  const ctx = useContext(WorkoutImportJobsContext);
  if (!ctx) {
    throw new Error(
      "useWorkoutImportJobs must be used within WorkoutImportJobsProvider",
    );
  }
  return ctx;
}
